import React, { useState, useEffect } from "react";
import { ChevronDown, Sparkles, ArrowRight } from "lucide-react";
import { BackgroundVideo } from "@/components/public/VideoPlayer";

function useYogyakartaTime() {
    const [time, setTime] = useState("");

    useEffect(() => {
        const update = () => {
            try {
                const formatter = new Intl.DateTimeFormat("en-GB", {
                    timeZone: "Asia/Jakarta",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });
                setTime(formatter.format(new Date()));
            } catch {
                const now = new Date();
                const h = String((now.getUTCHours() + 7) % 24).padStart(2, "0");
                const m = String(now.getUTCMinutes()).padStart(2, "0");
                setTime(`${h}:${m}`);
            }
        };
        update();
        const timer = setInterval(update, 1000);
        return () => clearInterval(timer);
    }, []);

    return time;
}

export default function Hero({ profile }) {
    const localTime = useYogyakartaTime();
    if (!profile) return null;

    return (
        <section
            data-testid="hero-section"
            className="relative w-full h-[100svh] min-h-[640px] overflow-hidden flex flex-col justify-between"
        >
            {/* Background video */}
            <BackgroundVideo
                type={profile.hero_video_type}
                url={profile.hero_video_url}
                poster={profile.hero_poster_url}
            />

            {/* Cinematic overlay: smooth dark gradient at bottom protects text legibility over any video */}
            <div
                className="absolute inset-0 bg-gradient-to-t from-ink-950/95 via-ink-950/65 via-35% to-transparent pointer-events-none z-[2]"
                aria-hidden
            />
            <div className="absolute inset-x-0 top-0 h-24 sm:h-32 bg-gradient-to-b from-black/60 via-black/20 to-transparent pointer-events-none z-[2]" aria-hidden />

            {/* Spacer for top navbar */}
            <div className="h-14 sm:h-20" />

            {/* Centerpiece Hero Content — positioned in the lower-third for cinematic video framing */}
            <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-6 lg:px-10 mt-auto mb-3 sm:mb-10">
                <div className="max-w-3xl space-y-2 sm:space-y-4">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 py-0.5 sm:px-3.5 sm:py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-[10px] sm:text-xs font-mono tracking-wider uppercase text-ink-100 shadow-sm">
                        <Sparkles size={11} className="text-brand animate-pulse" />
                        <span>{profile.title || "Founder & Principal Architect at Kodekami"}</span>
                    </div>

                    {/* Headline — balanced, elegant & editorial */}
                    <h1 className="font-display text-2xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.08] drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
                        Akhmad{" "}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand via-orange-400 to-amber-300">
                            Ferdiansyah
                        </span>
                    </h1>

                    {/* Subheadline / Tagline */}
                    <p className="text-xs sm:text-base md:text-lg text-ink-100/90 max-w-xl font-normal leading-relaxed line-clamp-2 sm:line-clamp-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                        {profile.tagline || "Engineering intelligent web applications, AI automations, and scalable commerce systems."}
                    </p>

                    {/* Action buttons — compact 1-row layout on mobile */}
                    <div className="pt-1 sm:pt-2 flex items-center gap-2 sm:gap-3 max-w-md">
                        <a
                            href="#work"
                            onClick={(e) => {
                                e.preventDefault();
                                document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" });
                            }}
                            className="group flex-1 sm:flex-initial inline-flex justify-center items-center gap-1.5 sm:gap-2.5 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-brand text-white text-xs sm:text-sm font-medium hover:bg-brand-hover transition-all shadow-[0_8px_25px_rgba(255,69,0,0.35)] hover:shadow-[0_12px_35px_rgba(255,69,0,0.45)] whitespace-nowrap"
                        >
                            <span>Selected Work</span>
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </a>
                        <a
                            href="#contact"
                            onClick={(e) => {
                                e.preventDefault();
                                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                            }}
                            className="flex-1 sm:flex-initial inline-flex justify-center items-center gap-1.5 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white font-medium text-xs sm:text-sm hover:bg-black/60 hover:border-white/30 transition-all shadow-sm whitespace-nowrap"
                        >
                            Get in Touch
                        </a>
                    </div>

                    {/* Minimalist specialization line — hidden on mobile to keep video visible */}
                    <div className="hidden sm:flex pt-2 flex-wrap items-center gap-x-3 gap-y-1.5 text-xs font-mono text-ink-200 drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">
                        <span className="hover:text-white transition-colors">Web Apps & Architecture</span>
                        <span className="text-white/30">·</span>
                        <span className="hover:text-white transition-colors">AI Agents & Integrations</span>
                        <span className="text-white/30">·</span>
                        <span className="hover:text-white transition-colors">E-Commerce & Operations</span>
                    </div>
                </div>
            </div>

            {/* Bottom Bar: Status pill, Scroll Cue, Time marker */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pb-4 sm:pb-8 flex items-center justify-between">
                {/* Status pill at bottom-left */}
                <div className="flex items-center gap-2 sm:gap-3 px-2.5 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-white/[0.06] backdrop-blur-md border border-white/10">
                    <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                        {profile.available_for_hire && (
                            <>
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-emerald-400"></span>
                            </>
                        )}
                    </span>
                    <span className="mono-label text-emerald-300 text-[10px] sm:text-xs tracking-wider">
                        {profile.available_for_hire ? "Available for Projects · 2026" : "Currently booked"}
                    </span>
                </div>

                {/* Scroll cue */}
                <button
                    onClick={() => document.querySelector("#intro")?.scrollIntoView({ behavior: "smooth" })}
                    data-testid="hero-scroll-cue"
                    className="hidden md:flex flex-col items-center gap-1 text-ink-300 hover:text-white transition-colors group"
                >
                    <span className="mono-label text-[11px] tracking-widest text-ink-300 group-hover:text-white transition-colors">scroll</span>
                    <ChevronDown size={16} className="animate-bounce text-ink-300 group-hover:text-white transition-colors" />
                </button>

                {/* Live Location & Local Time bottom-right */}
                <div
                    data-testid="hero-local-time"
                    title="Yogyakarta, Indonesia · Western Indonesia Time (UTC+7 / Jakarta Time)"
                    className="hidden md:flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/[0.06] backdrop-blur-md border border-white/10 shadow-sm"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                    </span>
                    <span className="mono-label text-ink-200 text-xs tracking-wider">
                        Yogyakarta, ID · {localTime ? `${localTime} UTC+7` : "UTC+7"}
                    </span>
                </div>
            </div>
        </section>
    );
}
